import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FEATURES, LIMIT_DEFS } from '@goyone/shared';
import { api } from '../../lib/api';
import { money, plural, WORDS } from '../../lib/format';
import { Icon, type IconName } from '../../components/Icon';
import { Logo } from '../../components/Logo';

interface PublicPlan {
  id: string;
  code: string;
  name: string;
  description: string;
  price: string;
  currency: string;
  cycle: 'MONTHLY' | 'YEARLY';
  badge: string;
  features: string[];
  limits: Record<string, number>;
  trialDays: number;
}

/**
 * الحد بصيغة يقرأها الزائر.
 *
 * "1024" لمساحة التخزين رقم لا معنى له خارج ذهن من كتبه. الوحدة جزء من
 * المعلومة لا زينة عليها.
 */
function formatLimit(key: string, value: number): string {
  if (value === -1) return 'بلا حد';
  if (key === 'storageMb') {
    return value >= 1024 ? `${Math.round(value / 1024)} جيجابايت` : `${value} ميجابايت`;
  }
  return new Intl.NumberFormat('ar-EG-u-nu-latn').format(value);
}

/*
 * كل ما في هذه الصفحة موجود في النظام اليوم.
 *
 * لا شعارات عملاء ولا آراء ولا أرقام استخدام: حين تتوفّر حقيقيةً تُضاف، وقبلها
 * يقوم الإقناع على ما يراه الزائر بعينه — لقطات من النظام نفسه وباقات تُقرأ
 * من القاعدة. وعدٌ واحد مكذوب على صفحة البيع يُسقط الصفحة كلها.
 */

const NAV = [
  { href: '#features', label: 'المزايا' },
  { href: '#how', label: 'كيف يعمل' },
  { href: '#pricing', label: 'الأسعار' },
  { href: '#faq', label: 'الأسئلة الشائعة' },
];

const INTEGRATIONS = ['واتساب', 'ماسنجر', 'إنستجرام', 'البريد الإلكتروني', 'Google Drive', 'اجتماعات الفيديو'];

const PROBLEMS: { pain: string; fix: string }[] = [
  {
    pain: 'بيانات العملاء موزّعة بين ملفات إكسل ومحادثات واتساب ودفاتر الموظفين.',
    fix: 'ملفّ واحد لكل عميل يجمع المكالمات والرسائل والصفقات والعروض والفواتير.',
  },
  {
    pain: 'عرض السعر يُكتب في وورد، والعقد في ملف آخر، والفاتورة في برنامج ثالث.',
    fix: 'العرض المقبول يصير عقدًا ثم فاتورة بهوية شركتك، وتُرحَّل الفاتورة إلى الدفاتر.',
  },
  {
    pain: 'لا تعرف ما الذي تأخّر اليوم ولا أيّ صفقة تحتاج تدخّلك.',
    fix: 'لوحة اليوم وغرفة العمليات تُظهران المتأخر والعالق قبل أن يتحوّل إلى خسارة.',
  },
  {
    pain: 'الحضور والإجازات والمرتبات على الورق أو في جداول لا يراها أحد.',
    fix: 'شؤون الموظفين في النظام نفسه، وكل موظف يرى إجازاته وطلباته ومرتبه بنفسه.',
  },
];

const MODULES: { icon: IconName; title: string; text: string; points: string[] }[] = [
  {
    icon: 'users',
    title: 'إدارة العملاء',
    text: 'من أول اتصال حتى التعاقد، بتاريخ كامل لكل عميل.',
    points: ['عملاء محتملون وحسابات وجهات اتصال', 'أنشطة وتقويم واجتماعات', 'غرفة عمليات تكشف ما يستحق التدخّل'],
  },
  {
    icon: 'chat',
    title: 'صندوق رسائل موحّد',
    text: 'رسائل عملائك من كل القنوات في مكان واحد.',
    points: ['واتساب وماسنجر وإنستجرام', 'بريد الشركة الوارد والصادر', 'ردود محفوظة لفريقك'],
  },
  {
    icon: 'funnel',
    title: 'المبيعات',
    text: 'صفقاتك وعروضك وعقودك وعمولات فريقك.',
    points: ['قمع مبيعات وتحليل للصفقات الخاسرة', 'عروض أسعار وعقود PDF بهوية شركتك', 'صفحات حجز وكتالوج خدمات وعمولات'],
  },
  {
    icon: 'wallet',
    title: 'الحسابات',
    text: 'من الفاتورة إلى التحصيل إلى القوائم المالية.',
    points: ['فواتير واشتراكات دورية وتحصيلات', 'موردون ومصروفات وبنوك وخزائن', 'قيود مزدوجة وتقادم ذمم وقوائم مالية'],
  },
  {
    icon: 'briefcase',
    title: 'الموارد البشرية',
    text: 'دورة الموظف كاملة، من الإعلان عن الوظيفة إلى المرتب.',
    points: ['صفحة وظائف وطلبات تقديم ومقابلات', 'حضور وإجازات وطلبات ومرتبات', 'تقييم أداء وخدمة ذاتية للموظف'],
  },
  {
    icon: 'gantt',
    title: 'المشاريع والمهام',
    text: 'تنفيذ ما بعته لعملائك، والتزاماتك الشهرية تجاههم.',
    points: ['لوحات مشاريع بالسحب والإفلات', 'باقات العملاء الشهرية: المتّفق عليه والمسلَّم', 'مهامي وحِمل الفريق'],
  },
  {
    icon: 'phone',
    title: 'مركز الاتصال',
    text: 'مكالمات فريقك مسجّلة ومقيّمة ومرتبطة بالعميل.',
    points: ['كونسول اتصال وسجلّ مكالمات', 'لوحة أداء وتقييم جودة المكالمات', 'اتصال من هاتف الموظف عبر تطبيق ويندوز'],
  },
  {
    icon: 'chartBar',
    title: 'التقارير',
    text: 'اسأل بياناتك ما تريد، واحفظ السؤال لفريقك.',
    points: ['باني تقارير: المقياس والتقسيم والشكل', 'تقارير محفوظة يعود إليها الفريق', 'تصدير إكسل وCSV وطباعة PDF'],
  },
];

/*
 * لقطات حقيقية من شركة الديمو، في public/landing.
 * تُحدَّث كلما تغيّرت الواجهة تغيّرًا ظاهرًا — اللقطة القديمة وعدٌ بما لم يعد موجودًا.
 */
const SHOTS: { key: string; label: string; caption: string; src: string }[] = [
  { key: 'home', label: 'لوحة اليوم', caption: 'كل صباح: ما يخصّك اليوم وما تأخّر وما يستحق المتابعة.', src: '/landing/home.jpg' },
  { key: 'pipeline', label: 'قمع المبيعات', caption: 'صفقاتك في مراحلها، بقيمة كل مرحلة ونسبة التحويل إلى ما بعدها.', src: '/landing/pipeline.jpg' },
  { key: 'invoice', label: 'الفواتير', caption: 'كل فاتورة بحالتها: المحصَّل والمتبقي والمتأخر، وإجمالي المستحق على عملائك.', src: '/landing/invoices.jpg' },
  { key: 'hr', label: 'الموارد البشرية', caption: 'ملفات الموظفين بأقسامهم ورواتبهم وأرصدة إجازاتهم في شاشة واحدة.', src: '/landing/hr.jpg' },
  { key: 'reports', label: 'التقارير', caption: 'اختر ما تقيسه وبأي تقسيم، واحفظه لفريقك.', src: '/landing/reports.jpg' },
];

const STEPS = [
  { title: 'سجّل شركتك', text: 'اسم الشركة ومعرّف قصير يصير رابطكم الخاص على goyone.com. التفعيل خلال دقيقة.' },
  { title: 'ادعُ فريقك', text: 'دعوة بالبريد لكل موظف، وصلاحيات محدّدة لكل دور: ما يراه وما يعدّله.' },
  { title: 'انقل بياناتك', text: 'استورد عملاءك وبياناتك من ملفات CSV بقوالب جاهزة ومعاينة قبل الحفظ.' },
  { title: 'اشتغل وتابع', text: 'فريقك يعمل في نظام واحد، وأنت ترى الصورة كاملة من لوحة اليوم والتقارير.' },
];

const REASONS: { icon: IconName; title: string; text: string }[] = [
  { icon: 'globe', title: 'عربي من الأساس', text: 'مصمَّم بالعربية ومن اليمين لليسار، لا ترجمة فوق برنامج أجنبي.' },
  { icon: 'layers', title: 'نظام واحد بدل خمسة', text: 'العملاء والمبيعات والحسابات والموظفون والمشاريع على قاعدة بيانات واحدة.' },
  { icon: 'building', title: 'رابط خاص لشركتك', text: 'كل شركة على نطاقها الفرعي، وبياناتها منفصلة عن غيرها.' },
  { icon: 'eye', title: 'حماية جادّة', text: 'تأكيد بالبريد عند الدخول من جهاز جديد، وتحقّق بخطوتين، وسجلّ تدقيق لكل تعديل.' },
  { icon: 'download', title: 'بياناتك ملكك', text: 'صدّرها في أي وقت إلى CSV أو إكسل، دون طلب ولا انتظار.' },
  { icon: 'wallet', title: 'أسعار بالجنيه', text: 'اشتراك شهري بالجنيه المصري، والدفع عبر إنستاباي أو فودافون كاش.' },
];

function faqs(trialDays: number) {
  return [
    {
      q: 'هل أحتاج بطاقة ائتمان لبدء التجربة؟',
      a: `لا. تبدأ ${plural(trialDays, WORDS.day)} بكل صلاحيات الباقة التي تختارها، دون أي بيانات دفع.`,
    },
    {
      q: 'ماذا يحدث بعد انتهاء الفترة التجريبية؟',
      a: 'يتواصل معك فريقنا لتفعيل الاشتراك. بياناتك وإعداداتك تبقى كما هي، فتكمل من حيث توقّفت.',
    },
    {
      q: 'كيف أدفع الاشتراك؟',
      a: 'من صفحة الاشتراك داخل النظام: تحوّل القيمة عبر إنستاباي أو فودافون كاش وترفق صورة الإيصال، وتصدر فاتورتك فور تأكيد التحويل.',
    },
    {
      q: 'هل أستطيع تغيير باقتي لاحقًا؟',
      a: 'نعم، تُرقّي باقتك من صفحة الاشتراك متى احتجت وحدات أو مستخدمين أكثر.',
    },
    {
      q: 'هل أستطيع نقل بياناتي الحالية؟',
      a: 'نعم. صفحة «تصدير واستيراد» تقبل ملفات CSV بقوالب جاهزة، وتعرض لك البيانات قبل حفظها.',
    },
    {
      q: 'كيف يدخل فريقي إلى النظام؟',
      a: 'كل شركة لها رابط خاص (اسم-شركتك.goyone.com). تدعو الموظف ببريده، فيصله رابط يختار منه كلمة مروره.',
    },
    {
      q: 'هل بياناتي آمنة؟',
      a: 'الدخول من جهاز جديد يحتاج رمزًا يصل إلى البريد، ويمكن تفعيل التحقّق بخطوتين، وكل تعديل مهم يُسجَّل في سجلّ التدقيق. وحذف الأعمال المكتملة يحتاج كلمة حماية منفصلة.',
    },
    {
      q: 'ماذا لو ألغيت الاشتراك؟',
      a: 'تُحفظ بياناتك 90 يومًا بعد الإلغاء قبل حذفها نهائيًا، وتستطيع تصديرها في أي وقت قبل ذلك.',
    },
  ];
}

/**
 * الصفحة التعريفية على النطاق الجذر.
 *
 * رحلة بيع كاملة لا جدول أسعار: المشكلة ثم الحل ثم الدليل ثم السعر. الباقات
 * تأتي من قاعدة البيانات لا من ثوابت في الواجهة: تعديل سعر أو وحدة من لوحة
 * المنصة يجب أن يظهر هنا فورًا، وإلا صار الموقع يعد بما لا يُسلَّم.
 */
export function Landing() {
  const plans = useQuery({
    queryKey: ['public', 'plans'],
    queryFn: () => api<PublicPlan[]>('/public/plans', { skipAuth: true }),
  });
  const [shot, setShot] = useState(SHOTS[0]!.key);
  const [menuOpen, setMenuOpen] = useState(false);

  const trialDays = plans.data?.[0]?.trialDays ?? 14;
  const startLabel = `ابدأ ${plural(trialDays, WORDS.day)} مجانًا`;
  const active = SHOTS.find((s) => s.key === shot) ?? SHOTS[0]!;

  return (
    <div className="mk">
      <header className="mk-nav">
        <div className="mk-wrap mk-nav-in">
          <a href="#top" className="mk-brand" aria-label="GoyOne">
            <Logo size={30} />
            <span>GoyOne</span>
          </a>
          <nav className={`mk-links${menuOpen ? ' is-open' : ''}`} onClick={() => setMenuOpen(false)}>
            {NAV.map((n) => (
              <a key={n.href} href={n.href}>
                {n.label}
              </a>
            ))}
            <Link to="/login" className="mk-links-login">
              تسجيل الدخول
            </Link>
          </nav>
          <div className="mk-nav-cta">
            <Link to="/login" className="mk-login">
              تسجيل الدخول
            </Link>
            <Link to="/signup" className="mk-btn mk-btn-primary">
              ابدأ مجانًا
            </Link>
            <button
              type="button"
              className="mk-menu"
              aria-label="القائمة"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              <Icon name={menuOpen ? 'x' : 'menu'} size={22} />
            </button>
          </div>
        </div>
      </header>

      <section className="mk-hero" id="top">
        <div className="mk-wrap">
          <span className="mk-eyebrow">نظام إدارة أعمال عربي متكامل</span>
          <h1>
            شركتك كلها في نظام واحد،
            <br />
            <em>بدل خمسة برامج متفرقة.</em>
          </h1>
          <p className="mk-lead">
            العملاء والمبيعات والفواتير والحسابات والموظفون والمشاريع على قاعدة بيانات واحدة — بالعربية، ومصمَّم
            لشركات الخدمات في مصر والخليج.
          </p>
          <div className="mk-cta-row">
            <Link to="/signup" className="mk-btn mk-btn-primary mk-btn-lg">
              {startLabel}
              <Icon name="arrowLeft" size={18} />
            </Link>
            <a href="#showcase" className="mk-btn mk-btn-ghost mk-btn-lg">
              شاهد النظام من الداخل
            </a>
          </div>
          <ul className="mk-hero-notes">
            <li>
              <Icon name="check" size={15} /> بلا بطاقة ائتمان
            </li>
            <li>
              <Icon name="check" size={15} /> كل الصلاحيات أثناء التجربة
            </li>
            <li>
              <Icon name="check" size={15} /> تُفعَّل شركتك خلال دقيقة
            </li>
          </ul>

          <div className="mk-hero-shot">
            <BrowserFrame src={SHOTS[0]!.src} alt="لوحة اليوم في GoyOne" eager />
          </div>
        </div>
      </section>

      <section className="mk-trust">
        <div className="mk-wrap">
          <p>يتكامل مع القنوات التي يستخدمها عملاؤك وفريقك</p>
          <ul>
            {INTEGRATIONS.map((i) => (
              <li key={i}>{i}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mk-section">
        <div className="mk-wrap">
          <SectionHead
            kicker="المشكلة"
            title="الشركة تكبر، والبرامج تتكاثر، والصورة تضيع"
            text="كل قسم على أداته، ولا أحد يرى العميل كاملًا. هذا ما يحلّه GoyOne."
          />
          <div className="mk-problems">
            {PROBLEMS.map((p) => (
              <div key={p.pain} className="mk-problem">
                <div className="mk-pain">
                  <Icon name="x" size={16} />
                  <span>{p.pain}</span>
                </div>
                <div className="mk-fix">
                  <Icon name="check" size={16} />
                  <span>{p.fix}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mk-section mk-alt" id="features">
        <div className="mk-wrap">
          <SectionHead
            kicker="المزايا"
            title="كل ما تحتاجه شركة خدمات، متصل ببعضه"
            text="كل وحدة تعرف ما في الأخرى: الصفقة تعرف عروضها، والفاتورة تعرف عقدها، والموظف يعرف مهامه."
          />
          <div className="mk-modules">
            {MODULES.map((m) => (
              <article key={m.title} className="mk-module">
                <span className="mk-module-icon">
                  <Icon name={m.icon} size={22} />
                </span>
                <h3>{m.title}</h3>
                <p>{m.text}</p>
                <ul>
                  {m.points.map((pt) => (
                    <li key={pt}>
                      <Icon name="check" size={14} />
                      {pt}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="mk-section mk-showcase" id="showcase">
        <div className="mk-wrap">
          <SectionHead
            dark
            kicker="من داخل النظام"
            title="ما تراه هنا هو ما سيعمل عليه فريقك"
            text="لقطات حقيقية من شركة تجريبية على GoyOne، لا رسومات تخيّلية."
          />
          <div className="mk-tabs" role="tablist">
            {SHOTS.map((s) => (
              <button
                key={s.key}
                type="button"
                role="tab"
                aria-selected={s.key === shot}
                className={s.key === shot ? 'is-active' : ''}
                onClick={() => setShot(s.key)}
              >
                {s.label}
              </button>
            ))}
          </div>
          <BrowserFrame key={active.key} src={active.src} alt={active.label} />
          <p className="mk-shot-caption">{active.caption}</p>
        </div>
      </section>

      <section className="mk-section" id="how">
        <div className="mk-wrap">
          <SectionHead kicker="كيف يعمل" title="من التسجيل إلى العمل في أربع خطوات" />
          <ol className="mk-steps">
            {STEPS.map((s, i) => (
              <li key={s.title}>
                <span className="mk-step-num num">{i + 1}</span>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="mk-section mk-alt">
        <div className="mk-wrap">
          <SectionHead kicker="لماذا GoyOne" title="مبني لطريقة عمل شركاتنا" />
          <div className="mk-reasons">
            {REASONS.map((r) => (
              <div key={r.title} className="mk-reason">
                <Icon name={r.icon} size={22} />
                <div>
                  <h3>{r.title}</h3>
                  <p>{r.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mk-section" id="pricing">
        <div className="mk-wrap">
          <SectionHead
            kicker="الأسعار"
            title="باقات واضحة، تبدأ كلها بتجربة مجانية"
            text={`${plural(trialDays, WORDS.day)} بكل صلاحيات الباقة، بلا بطاقة ائتمان. رقِّ باقتك متى احتجت.`}
          />

          {plans.isPending && <div className="skeleton" style={{ height: 420 }} />}
          {plans.isError && (
            <p className="alert alert-danger">تعذّر تحميل الباقات. حدّث الصفحة أو تواصل معنا.</p>
          )}

          <div className="mk-plans">
            {plans.data?.map((p) => (
              <div key={p.id} className={`mk-plan${p.badge ? ' is-featured' : ''}`}>
                {p.badge && <span className="mk-plan-badge">{p.badge}</span>}
                <h3>{p.name}</h3>
                <p className="mk-plan-desc">{p.description}</p>

                <div className="mk-plan-price">
                  <span className="num">{money(p.price, p.currency === 'EGP' ? 'ج.م' : p.currency)}</span>
                  <span>/ {p.cycle === 'YEARLY' ? 'سنويًا' : 'شهريًا'}</span>
                </div>

                <Link
                  to={`/signup?plan=${p.code}`}
                  className={`mk-btn mk-btn-block ${p.badge ? 'mk-btn-primary' : 'mk-btn-outline'}`}
                >
                  ابدأ التجربة المجانية
                </Link>

                <ul className="mk-plan-list">
                  {FEATURES.filter((f) => p.features.includes(f.key)).map((f) => (
                    <li key={f.key}>
                      <Icon name="check" size={15} />
                      {f.label}
                    </li>
                  ))}
                </ul>

                <div className="mk-plan-limits">
                  {/*
                    الحدود الصفرية لا تُعرض.
                    "رسائل واتساب: صفر" على صفحة أسعار تُقرأ وعدًا مكسورًا لا
                    حدًا. ما لا تشمله الباقة يغيب عن قائمتها لا يظهر بصفر.
                  */}
                  {LIMIT_DEFS.filter((l) => {
                    const v = p.limits?.[l.key];
                    return v !== undefined && v !== 0;
                  }).map((l) => (
                    <div key={l.key}>
                      <span>{l.label}</span>
                      <span className="num">{formatLimit(l.key, p.limits[l.key]!)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mk-section mk-alt" id="faq">
        <div className="mk-wrap mk-faq-wrap">
          <SectionHead kicker="الأسئلة الشائعة" title="قبل أن تبدأ" />
          <div className="mk-faq">
            {faqs(trialDays).map((f) => (
              <details key={f.q}>
                <summary>
                  {f.q}
                  <Icon name="plus" size={18} />
                </summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="mk-final">
        <div className="mk-wrap">
          <h2>جرّب GoyOne على شركتك أنت</h2>
          <p>سجّل في دقيقة، وادعُ فريقك، واحكم بنفسك بعد {plural(trialDays, WORDS.day)}.</p>
          <div className="mk-cta-row">
            <Link to="/signup" className="mk-btn mk-btn-primary mk-btn-lg">
              {startLabel}
              <Icon name="arrowLeft" size={18} />
            </Link>
            <Link to="/login" className="mk-btn mk-btn-ghost mk-btn-lg">
              لديّ حساب — تسجيل الدخول
            </Link>
          </div>
        </div>
      </section>

      <footer className="mk-foot">
        <div className="mk-wrap mk-foot-in">
          <div className="mk-foot-brand">
            <div className="mk-brand">
              <Logo size={28} />
              <span>GoyOne</span>
            </div>
            <p>نظام إدارة الأعمال المتكامل لشركات الخدمات.</p>
          </div>
          <div className="mk-foot-col">
            <h4>المنتج</h4>
            {NAV.map((n) => (
              <a key={n.href} href={n.href}>
                {n.label}
              </a>
            ))}
          </div>
          <div className="mk-foot-col">
            <h4>الحساب</h4>
            <Link to="/signup">إنشاء حساب شركة</Link>
            <Link to="/login">تسجيل الدخول</Link>
            <Link to="/forgot">نسيت كلمة المرور</Link>
          </div>
          <div className="mk-foot-col">
            <h4>تواصل معنا</h4>
            <a href="mailto:support@goyone.com">support@goyone.com</a>
            <a href="tel:+201146293399" dir="ltr">
              +20 114 629 3399
            </a>
          </div>
        </div>
        <div className="mk-wrap mk-foot-bottom">
          <span>© {new Date().getFullYear()} GoyOne</span>
        </div>
      </footer>
    </div>
  );
}

function SectionHead({ kicker, title, text, dark }: { kicker: string; title: string; text?: string; dark?: boolean }) {
  return (
    <div className={`mk-head${dark ? ' is-dark' : ''}`}>
      <span className="mk-kicker">{kicker}</span>
      <h2>{title}</h2>
      {text && <p>{text}</p>}
    </div>
  );
}

/**
 * إطار متصفح حول اللقطة: يقول للزائر «هذا برنامج يعمل» قبل أن يقرأ حرفًا.
 * لقطة لم تُرفع بعد يغيب إطارها كله؛ إطار فارغ أسوأ من لا إطار.
 */
function BrowserFrame({ src, alt, eager }: { src: string; alt: string; eager?: boolean }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <figure className="mk-frame">
      <div className="mk-frame-bar" aria-hidden>
        <i />
        <i />
        <i />
        <span dir="ltr">your-company.goyone.com</span>
      </div>
      <img
        src={src}
        alt={alt}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        onError={() => setFailed(true)}
      />
    </figure>
  );
}
